import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface ListQueryState {
  page: number
  pageSize: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  search?: string
  dateFrom?: string
  dateTo?: string
  filters: Record<string, string>
}

export interface ListQueryDefaults {
  pageSize?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  filters?: Record<string, string>
}

const RESERVED = new Set(['page', 'pageSize', 'sortBy', 'sortDir', 'search', 'dateFrom', 'dateTo'])

export function useListQuery(defaults: ListQueryDefaults = {}) {
  const [params, setParams] = useSearchParams()

  const state: ListQueryState = useMemo(() => {
    const filters: Record<string, string> = { ...(defaults.filters ?? {}) }
    params.forEach((v, k) => {
      if (!RESERVED.has(k)) filters[k] = v
    })
    const pageStr = params.get('page')
    const pageSizeStr = params.get('pageSize')
    return {
      page: pageStr ? Math.max(0, Number(pageStr)) : 0,
      pageSize: pageSizeStr ? Number(pageSizeStr) : (defaults.pageSize ?? 50),
      sortBy: params.get('sortBy') ?? defaults.sortBy,
      sortDir: (params.get('sortDir') as 'asc' | 'desc' | null) ?? defaults.sortDir,
      search: params.get('search') ?? undefined,
      dateFrom: params.get('dateFrom') ?? undefined,
      dateTo: params.get('dateTo') ?? undefined,
      filters,
    }
  }, [params, defaults.pageSize, defaults.sortBy, defaults.sortDir, defaults.filters])

  const merge = useCallback((patch: Partial<ListQueryState>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (patch.page !== undefined) {
        if (patch.page === 0) next.delete('page')
        else next.set('page', String(patch.page))
      }
      if (patch.pageSize !== undefined) next.set('pageSize', String(patch.pageSize))
      if (patch.sortBy !== undefined) {
        if (!patch.sortBy) next.delete('sortBy')
        else next.set('sortBy', patch.sortBy)
      }
      if (patch.sortDir !== undefined) next.set('sortDir', patch.sortDir)
      if (patch.search !== undefined) {
        if (!patch.search) next.delete('search')
        else next.set('search', patch.search)
      }
      if (patch.dateFrom !== undefined) {
        if (!patch.dateFrom) next.delete('dateFrom')
        else next.set('dateFrom', patch.dateFrom)
      }
      if (patch.dateTo !== undefined) {
        if (!patch.dateTo) next.delete('dateTo')
        else next.set('dateTo', patch.dateTo)
      }
      if (patch.filters) {
        for (const [k, v] of Object.entries(patch.filters)) {
          if (!v) next.delete(k)
          else next.set(k, v)
        }
      }
      return next
    }, { replace: true })
  }, [setParams])

  return {
    state,
    setPage: (p: number) => merge({ page: p }),
    setPageSize: (s: number) => merge({ pageSize: s, page: 0 }),
    setSort: (sortBy: string, sortDir: 'asc' | 'desc') => merge({ sortBy, sortDir, page: 0 }),
    toggleSort: (key: string) => {
      if (state.sortBy === key) merge({ sortDir: state.sortDir === 'asc' ? 'desc' : 'asc', page: 0 })
      else merge({ sortBy: key, sortDir: 'desc', page: 0 })
    },
    setSearch: (s: string) => merge({ search: s, page: 0 }),
    setDateRange: (from: string, to: string) => merge({ dateFrom: from, dateTo: to, page: 0 }),
    setFilter: (key: string, value: string) => merge({ filters: { [key]: value }, page: 0 }),
  }
}
